import type { I18n } from '@lingui/core';
import { useLingui } from '@lingui/react';

import { GeoCoordinates, PostalAddress, schema } from '@rover/components/src/microdata';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { ServiceType } from '@rover/constants/src/translations/service.constants';
import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';
import type { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';
import { ServiceAddOnTypeSlugs } from '@rover/types/src/ServiceAddOn';
import {
  coercePriceTransparencyLevel,
  getIsPriceTransparency,
} from '@rover/utilities/priceTransparency';

import { formatPrice } from './ServiceSection/utils';

type Props = {
  url: string;
  imageUrl: string;
  locale: Locale;
  services: EnhancedPersonSitterProfileService[];
  isCatOnly?: boolean;
};

function getPriceRange(
  service: EnhancedPersonSitterProfileService | undefined,
  locale: Locale,
  i18n: I18n
): string | undefined {
  const standardRate = service?.serviceAddOns.find(
    (sa) => sa.slug === ServiceAddOnTypeSlugs.standardRate && sa.active
  );
  if (!standardRate) return undefined;

  // Display full price including fees for any PT markets (lite or full), using the
  // service's own price transparency level.
  const isFullPrice = getIsPriceTransparency(
    coercePriceTransparencyLevel(service?.priceTransparencyLevel)
  );
  const formattedPrice = formatPrice(i18n, standardRate, locale, isFullPrice);
  // TODO: Make this a better translated string when we get improved copy, fine for MVP
  return formattedPrice + standardRate.unitLabel;
}

const SitterProfileLocalBusinessSchema: React.FC<Props> = ({
  url,
  imageUrl,
  locale,
  services,
  isCatOnly,
}: Props) => {
  const { i18n } = useLingui();
  const primarySlug = isCatOnly ? ServiceType.DROP_IN : ServiceType.OVERNIGHT_BOARDING;
  const primaryService = services.find((s) => s.slug === primarySlug);
  const priceRange = getPriceRange(primaryService, locale, i18n);
  const serviceMeta = primaryService?.meta;

  return (
    <>
      <meta itemProp="url" content={url} />
      <meta itemProp="image" content={imageUrl} />
      <meta itemProp="photo" content={imageUrl} />
      <meta itemProp="additionalType" content="http://www.productontology.org/id/Pet_sitting" />
      <meta itemProp="openingHours" content="As needed" />
      {priceRange && <meta itemProp="priceRange" content={priceRange} />}
      {serviceMeta?.city && serviceMeta?.state && (
        <PostalAddress
          itemProp="address"
          localityType={serviceMeta.neighborhood ? 'neighborhood' : 'city'}
          name={serviceMeta.neighborhood || serviceMeta.city}
          cityName={serviceMeta.city}
          regionName={serviceMeta.state}
          countryCode={serviceMeta.countryCode ?? undefined}
          postalCode={serviceMeta.zip ?? undefined}
        />
      )}
      {serviceMeta?.latitude && serviceMeta?.longitude && (
        <GeoCoordinates
          itemProp="geo"
          latitude={serviceMeta.latitude}
          longitude={serviceMeta.longitude}
        />
      )}
      <A11yHiddenBox
        webProps={{
          itemScope: true,
          itemType: schema`ReserveAction`,
          itemProp: 'potentialAction',
        }}
      >
        <span itemScope itemType={schema`EntryPoint`} itemProp="target">
          <meta itemProp="urlTemplate" content={url} />
          <meta itemProp="inLanguage" content={locale} />
          <meta itemProp="actionPlatform" content="http://schema.org/DesktopWebPlatform" />
          <meta itemProp="actionPlatform" content="http://schema.org/IOSPlatform" />
          <meta itemProp="actionPlatform" content="http://schema.org/AndroidPlatform" />
        </span>
        <span itemScope itemType={schema`Reservation`} itemProp="result">
          <meta itemProp="name" content={isCatOnly ? 'Book a cat sitter' : 'Book a pet sitter'} />
        </span>
      </A11yHiddenBox>
    </>
  );
};

export default SitterProfileLocalBusinessSchema;
